import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Input,
	ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { ReplaySubject } from 'rxjs';
import {
	concatMap,
	filter,
	finalize,
	switchMap,
	tap,
	withLatestFrom,
} from 'rxjs/operators';
import { animateList } from 'src/shared/animations';
import { JobCostCenter, JobsClient } from 'src/shared/api';
import { ObservableDataSource } from 'src/shared/observable-data-source';
import { AddJobCostCenterDialogComponent } from './add-job-cost-center-dialog.component';

type AddCostCenter = JobCostCenter & { addAnother: boolean };

@Component({
	selector: 'rpfsa-cost-center-list',
	templateUrl: './cost-center-list.component.html',
	styleUrls: ['./cost-center-list.component.scss'],
	animations: [animateList],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CostCenterListComponent {
	@ViewChild(MatSort) set sort(value: MatSort) {
		this.dataSource.sort = value;
	}
	@ViewChild(MatPaginator) set paginator(value: MatPaginator) {
		this.dataSource.paginator = value;
	}

	private readonly _jobId = new ReplaySubject<number>(1);
	@Input() set jobId(value: number) {
		this._jobId.next(value);
	}

	readonly dataSource = new ObservableDataSource<JobCostCenter>(
		this._jobId.pipe(switchMap((id) => this.jobsClient.getCostCenters(id)))
	);

	readonly resultColumns = ['code', 'description', 'milePostRange', 'delete'];

	readonly deletingMap: { [key: number]: boolean } = {};

	constructor(
		private readonly jobsClient: JobsClient,
		private readonly snackbar: MatSnackBar,
		private readonly dialog: MatDialog,
		private readonly cd: ChangeDetectorRef
	) {}

	openAddCostCenterDialog() {
		this.dialog
			.open<AddJobCostCenterDialogComponent, never, AddCostCenter>(
				AddJobCostCenterDialogComponent,
				{ width: '450px' }
			)
			.afterClosed()
			.pipe(
				filter<AddCostCenter>(Boolean),
				tap(({ addAnother }) =>
					addAnother ? this.openAddCostCenterDialog() : null
				),
				withLatestFrom(this._jobId, (costCenter, jobId) => ({
					...costCenter,
					job: { id: jobId },
				})),
				concatMap((costCenter) =>
					this.jobsClient.addJobCostCenter(costCenter)
				),
				concatMap(() => this.dataSource.refresh())
			)
			.subscribe(() => this.snackbar.open('Cost Center Added'));
	}

	deleteCostCenter(costCenter: JobCostCenter, index: number) {
		if (!this.deletingMap[index]) {
			this.deletingMap[index] = true;

			this.jobsClient
				.deleteJobCostCenter(costCenter.id)
				.pipe(
					concatMap(() => this.dataSource.refresh()),
					finalize(() => {
						this.deletingMap[index] = false;
						this.cd.detectChanges();
					})
				)
				.subscribe(() => {
					this.snackbar.open('Cost Center deleted');
				});
		}
	}
}
